import { unwrap } from "@sagot/api-client";
import { API_KEY_DEFAULT_EXPIRY, API_KEY_EXPIRY_DAYS, API_KEY_LAUNCH_SCOPES, API_KEY_NAME_MAX_LENGTH, API_KEY_SCOPES, SECURE_LAUNCH_FLAG, type ApiKeyExpiry, type ApiKeyScope } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Checkbox, Dialog, DialogContent, Field, Input, Select, toast } from "@sagot/ui";
import { useState, type SyntheticEvent } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { usePermissions } from "../../../../session/permissions";
import { useApi } from "../../../../session/session-provider";
import { useBillingOverview } from "../../shared";
import { IpRangesField } from "./key-allowlist-dialog";
import { SecretOnce } from "./panel-kit";

const EXPIRY_KEYS = Object.keys(API_KEY_EXPIRY_DAYS) as ApiKeyExpiry[];
const LAUNCH_SCOPES: readonly string[] = API_KEY_LAUNCH_SCOPES;

/** Toggle one scope in a selection, keeping catalogue order. */
export function toggleScope(selected: readonly ApiKeyScope[], scope: ApiKeyScope, on: boolean): ApiKeyScope[] {
  const next = new Set(selected);
  if (on) next.add(scope);
  else next.delete(scope);
  return API_KEY_SCOPES.filter((s) => next.has(s));
}

/** The scopes a workspace may pick: the Secure launch ones only when it has Secure launch (or the key already asks for them). */
export function offeredScopes(secureLaunch: boolean, selected: readonly ApiKeyScope[] = []): ApiKeyScope[] {
  return API_KEY_SCOPES.filter((scope) => secureLaunch || !LAUNCH_SCOPES.includes(scope) || selected.includes(scope));
}

interface CreateKeyDialogProps {
  onClose: () => void;
  onCreated: () => Promise<unknown>;
  /** Scopes ticked when the dialog opens (the Secure launch card starts from the launch scopes). */
  initialScopes?: readonly ApiKeyScope[];
  initialName?: string;
}

/** Whether the workspace has Secure launch (plan or add-on); needs `billing.view`, so a viewer without it sees the base scopes. */
function useSecureLaunch(): boolean {
  const permissions = usePermissions();
  const overview = useBillingOverview(permissions.can("billing.view"));
  return overview.data?.features.includes(SECURE_LAUNCH_FLAG) ?? false;
}

/** Two steps: name + scopes + expiry + optional IP allowlist -> the full key, shown exactly once. */
export function CreateKeyDialog({ onClose, onCreated, initialScopes = ["chat:write"], initialName = "" }: CreateKeyDialogProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const secureLaunch = useSecureLaunch();
  const [name, setName] = useState(initialName);
  const [scopes, setScopes] = useState<ApiKeyScope[]>([...initialScopes]);
  const [expiry, setExpiry] = useState<ApiKeyExpiry>(API_KEY_DEFAULT_EXPIRY);
  const [ranges, setRanges] = useState<string[]>([]);
  const [secret, setSecret] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [nameError, setNameError] = useState<string | undefined>();

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setNameError(t("settings.api.nameRequired"));
      return;
    }
    setBusy(true);
    try {
      const created = await unwrap(api.POST("/v1/api-keys", { body: { name: name.trim(), scopes, expires_in_days: API_KEY_EXPIRY_DAYS[expiry], allowed_ip_ranges: ranges } }));
      setSecret(created.key);
      await onCreated();
    } catch (caught) {
      toast.error(message(caught));
    } finally {
      setBusy(false);
    }
  };

  if (secret) {
    return (
      <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent title={t("settings.api.secretTitle")} closeLabel={t("common.close")} footer={<Button onClick={onClose}>{t("settings.api.saved")}</Button>}>
          <SecretOnce value={secret} label={t("settings.api.secretLabel")} warning={<><strong>{t("settings.api.secretWarningStrong")}</strong> {t("settings.api.secretWarning")}</>} />
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent title={t("settings.api.createTitle")} closeLabel={t("common.close")}>
        <form className="space-y-4" onSubmit={(event) => void submit(event)} noValidate>
          <Field label={t("settings.api.name")} error={nameError}>
            <Input value={name} maxLength={API_KEY_NAME_MAX_LENGTH} placeholder={t("settings.api.namePlaceholder")} onChange={(event) => { setName(event.target.value); setNameError(undefined); }} />
          </Field>
          <fieldset>
            <legend className="label">{t("settings.api.scopes")}</legend>
            <div className="space-y-2">
              {offeredScopes(secureLaunch, initialScopes).map((scope) => (
                <div key={scope} className="rounded-md border border-line px-3 py-2.5">
                  <Checkbox
                    checked={scopes.includes(scope)}
                    onCheckedChange={(checked) => { setScopes((current) => toggleScope(current, scope, checked === true)); }}
                    label={<span className="font-mono text-xs font-semibold">{scope}</span>}
                    description={t(`settings.api.scopeHelp.${scope.replace(":", "_")}`)}
                  />
                </div>
              ))}
            </div>
          </fieldset>
          <IpRangesField ranges={ranges} onChange={setRanges} label={t("settings.api.createAllowlist")} hint={t("settings.api.createAllowlistHint")} />
          <Field label={t("settings.api.expires")}>
            <Select value={expiry} onChange={(event) => { setExpiry(event.target.value as ApiKeyExpiry); }}>
              {EXPIRY_KEYS.map((key) => (
                <option key={key} value={key}>
                  {t(`settings.api.expiry.${key}`)}
                </option>
              ))}
            </Select>
          </Field>
          <div className="flex justify-end gap-2 border-t border-line pt-3">
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={busy} disabled={scopes.length === 0}>
              {t("settings.api.create")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
